import deleteServiceAccountRequest from '@cartwhl/common/api/deleteServiceAccount';
import getAdminUserManagementDetail from '@cartwhl/common/api/getAdminUserManagementDetail';
import type {
  AdminUserManagementOrganizationDetail,
  AdminUserManagementUserDetail,
} from '@cartwhl/common/api/getAdminUserManagementDetail';
import getAdminUserManagementOrganizations, {
  MANUAL_ORG_CREATION_STYTCH_ID,
  SELF_SERVICE_CREATION_STYTCH_ID,
} from '@cartwhl/common/api/getAdminUserManagementOrganizations';
import type { AdminUserManagementOrganizationSummary } from '@cartwhl/common/api/getAdminUserManagementOrganizations';
import getAdminUserManagementSearch from '@cartwhl/common/api/getAdminUserManagementSearch';
import type { AdminUserManagementSearchResult } from '@cartwhl/common/api/getAdminUserManagementSearch';
import postAdminGrantOrgCredits from '@cartwhl/common/api/postAdminGrantOrgCredits';
import postAdminUserManagementActions from '@cartwhl/common/api/postAdminUserManagementActions';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';

import * as globalStores from '../../stores/global';

type Deps = { global: typeof globalStores };

type State = {
  searchQuery: string;
  searchResult: AdminUserManagementSearchResult | null;
  searchLoading: boolean;
  searchError: string | null;
  viewMode: 'search' | 'user' | 'organization';
  userDetail: AdminUserManagementUserDetail | null;
  organizationDetail: AdminUserManagementOrganizationDetail | null;
  detailLoading: boolean;
  detailError: string | null;
  actionLoading: boolean;
  manualOrganizations: AdminUserManagementOrganizationSummary[] | null;
  manualOrganizationsLoading: boolean;
  manualOrganizationsError: string | null;
};

const initialState: State = {
  searchQuery: '',
  searchResult: null,
  searchLoading: false,
  searchError: null,
  viewMode: 'search',
  userDetail: null,
  organizationDetail: null,
  detailLoading: false,
  detailError: null,
  actionLoading: false,
  manualOrganizations: null,
  manualOrganizationsLoading: false,
  manualOrganizationsError: null,
};

class AdminUserManagementStore extends BaseStore<State, Deps> {
  constructor(deps: Deps) {
    super(initialState, deps);
  }

  init() {}

  reset() {
    this.state.searchQuery = initialState.searchQuery;
    this.state.searchResult = initialState.searchResult;
    this.state.searchLoading = initialState.searchLoading;
    this.state.searchError = initialState.searchError;
    this.state.viewMode = initialState.viewMode;
    this.state.userDetail = initialState.userDetail;
    this.state.organizationDetail = initialState.organizationDetail;
    this.state.detailLoading = initialState.detailLoading;
    this.state.detailError = initialState.detailError;
    this.state.actionLoading = initialState.actionLoading;
    this.state.manualOrganizations = initialState.manualOrganizations;
    this.state.manualOrganizationsLoading =
      initialState.manualOrganizationsLoading;
    this.state.manualOrganizationsError = initialState.manualOrganizationsError;
  }

  async loadManualOrganizations() {
    this.state.manualOrganizationsLoading = true;
    this.state.manualOrganizationsError = null;
    this.state.manualOrganizations = null;

    const [manualRes, selfServiceRes] = await Promise.all([
      getAdminUserManagementOrganizations({
        stytchID: MANUAL_ORG_CREATION_STYTCH_ID,
      }),
      getAdminUserManagementOrganizations({
        stytchID: SELF_SERVICE_CREATION_STYTCH_ID,
      }),
    ]);

    this.state.manualOrganizationsLoading = false;
    const firstError = manualRes.error ?? selfServiceRes.error;
    if (firstError) {
      this.state.manualOrganizationsError = firstError.message;
      return;
    }

    const byId = new Map<string, AdminUserManagementOrganizationSummary>();
    for (const org of [
      ...(manualRes.data?.organizations ?? []),
      ...(selfServiceRes.data?.organizations ?? []),
    ]) {
      byId.set(org.organizationID, org);
    }
    const combined = [...byId.values()].sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime(),
    );
    this.state.manualOrganizations = combined;
  }

  setSearchQuery(query: string) {
    this.state.searchQuery = query;
  }

  async search() {
    const q = this.state.searchQuery.trim();
    if (!q) return;

    this.state.searchLoading = true;
    this.state.searchError = null;
    this.state.searchResult = null;

    const { data, error } = await getAdminUserManagementSearch(q);

    this.state.searchLoading = false;
    if (error) {
      this.state.searchError = error.message;
      return;
    }
    this.state.searchResult = data ?? null;

    if (data) {
      if (data.type === 'user') {
        await this.loadUserDetail(
          (data.user as { userID?: string }).userID ?? '',
        );
      } else if (data.type === 'organization') {
        await this.loadOrganizationDetail(
          (data.organization as { organizationID?: string }).organizationID ??
            '',
        );
      } else if (data.type === 'service') {
        await this.loadOrganizationDetail(data.serviceAccount.organizationID);
      }
    }
  }

  resetSearch() {
    this.state.searchResult = null;
    this.state.searchError = null;
  }

  goToSearch() {
    this.state.viewMode = 'search';
    this.state.userDetail = null;
    this.state.organizationDetail = null;
    this.state.detailError = null;
  }

  async loadUserDetail(id: string) {
    this.state.viewMode = 'user';
    this.state.detailLoading = true;
    this.state.detailError = null;
    this.state.userDetail = null;
    this.state.organizationDetail = null;

    const { data, error } = await getAdminUserManagementDetail('user', id);

    this.state.detailLoading = false;
    if (error) {
      this.state.detailError = error.message;
      return;
    }
    this.state.userDetail = data as AdminUserManagementUserDetail;
  }

  async loadOrganizationDetail(id: string) {
    this.state.viewMode = 'organization';
    this.state.detailLoading = true;
    this.state.detailError = null;
    this.state.userDetail = null;
    this.state.organizationDetail = null;

    const { data, error } = await getAdminUserManagementDetail(
      'organization',
      id,
    );

    this.state.detailLoading = false;
    if (error) {
      this.state.detailError = error.message;
      return;
    }
    this.state.organizationDetail =
      data as AdminUserManagementOrganizationDetail;
  }

  async createOrganization(organizationName: string, creatorUserID: string) {
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'createOrganization',
      organizationName,
      creatorUserID,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return null;
    }
    if (data && 'organization' in data && data.organization?.organizationID) {
      this.deps.global.noticesStore.info('Organization created');
      return data.organization.organizationID as string;
    }
    return null;
  }

  async addUserToOrganization(
    organizationID: string,
    targetUserIDOrEmail: string,
    planType: 'free' | 'pro' | 'unlimited',
    accessLevel: 'EDIT' | 'VIEW',
  ) {
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'addUserToOrganization',
      organizationID,
      targetUserIDOrEmail,
      planType,
      accessLevel,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    if (data && 'organization' in data) {
      this.deps.global.noticesStore.info('User added to organization');
      return true;
    }
    return false;
  }

  async removeUserFromOrganization(
    organizationID: string,
    targetUserIDOrEmail: string,
  ) {
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'removeUserFromOrganization',
      organizationID,
      targetUserIDOrEmail,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    if (data && 'organization' in data) {
      this.deps.global.noticesStore.info('User removed from organization');
      return true;
    }
    return false;
  }

  async modifyOrganizationSeatCapacity(
    organizationID: string,
    planType: 'pro' | 'unlimited' | 'team',
    seatCapacityDelta: number,
  ) {
    if (
      !Number.isFinite(seatCapacityDelta) ||
      !Number.isInteger(seatCapacityDelta) ||
      seatCapacityDelta === 0
    ) {
      this.deps.global.noticesStore.error(
        'Seat change must be a non-zero whole number (use negative to reduce).',
      );
      return false;
    }
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'modifyOrganizationSeatCapacity',
      organizationID,
      planType,
      seatCapacityDelta,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    if (data && 'organization' in data) {
      this.deps.global.noticesStore.info('Seat capacity updated');
      return true;
    }
    return false;
  }

  async createServiceAccount(organizationID: string, serviceName: string) {
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'createServiceAccount',
      organizationID,
      serviceName,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return null;
    }
    if (data && 'apiKey' in data) {
      this.deps.global.noticesStore.info('API key created');
      return data;
    }
    return null;
  }

  async deleteServiceAccount(organizationID: string, serviceID: string) {
    this.state.actionLoading = true;
    const { error } = await deleteServiceAccountRequest(
      organizationID,
      serviceID,
    );
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    this.deps.global.noticesStore.info('API key deleted');
    await this.loadOrganizationDetail(organizationID);
    return true;
  }

  async updateServiceAccount(
    serviceID: string,
    organizationID: string,
    updates: { accountBalanceDeltaUSD?: number; invalidAfter?: string },
  ) {
    this.state.actionLoading = true;
    const { data, error } = await postAdminUserManagementActions({
      action: 'updateServiceAccount',
      serviceID,
      ...updates,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    if (data && 'serviceAccount' in data) {
      this.deps.global.noticesStore.info('Service account updated');
      await this.loadOrganizationDetail(organizationID);
      return true;
    }
    return false;
  }

  async grantManualOrgCredits(organizationID: string, creditsToGrant: number) {
    if (
      Number.isNaN(creditsToGrant) ||
      !Number.isInteger(creditsToGrant) ||
      creditsToGrant < 1 ||
      creditsToGrant >= 15000
    ) {
      this.deps.global.noticesStore.error(
        'Credits must be a whole number from 1 to 15000.',
      );
      return false;
    }
    this.state.actionLoading = true;
    const { data, error } = await postAdminGrantOrgCredits({
      organizationID,
      creditsToGrant,
    });
    this.state.actionLoading = false;

    if (error) {
      this.deps.global.noticesStore.error(error.message);
      return false;
    }
    if (data?.organization) {
      this.deps.global.noticesStore.info('Credits granted to organization');
      if (
        this.state.organizationDetail &&
        this.state.viewMode === 'organization'
      ) {
        this.state.organizationDetail = {
          ...this.state.organizationDetail,
          organization: data.organization as NonNullable<
            State['organizationDetail']
          >['organization'],
        };
      } else {
        await this.loadOrganizationDetail(organizationID);
      }
      return true;
    }
    return false;
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: AdminUserManagementStore,
  displayName: 'AdminUserManagementStore',
  staticDeps: { global: globalStores },
});
